import { Box, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { ForecastMode } from "../models/ForecastReport";

const navButtonSx = {
	bgcolor: "primary.main",
	color: "white",
	width: 28,
	height: 28,
	"&:hover": { bgcolor: "primary.dark" },
	"&.Mui-disabled": {
		bgcolor: "action.disabledBackground",
		color: "action.disabled",
	},
};

interface Props {
	startYear: number;
	endYear: number;
	mode: ForecastMode;
	onStartYearChange: (y: number) => void;
	onEndYearChange: (y: number) => void;
	onModeChange: (m: ForecastMode) => void;
	isLoading: boolean;
}

export const ForecastReportToolbar = ({
	startYear,
	endYear,
	mode,
	onStartYearChange,
	onEndYearChange,
	onModeChange,
	isLoading,
}: Props) => {
	const handleStartChange = (y: number) => {
		const next = y > endYear ? endYear : y;
		onStartYearChange(next);
	};

	const handleEndChange = (y: number) => {
		const next = y < startYear ? startYear : y;
		onEndYearChange(next);
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Tooltip title="Previous year">
					<span>
						<IconButton
							onClick={() => handleStartChange(startYear - 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronLeftIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Box sx={{ width: 100 }}>
					<TextField
						label="Start"
						value={String(startYear)}
						size="small"
						fullWidth
						slotProps={{
							input: { readOnly: true },
							inputLabel: { shrink: true },
							htmlInput: { style: { textAlign: "center", fontWeight: 600 } },
						}}
					/>
				</Box>
				<Tooltip title="Next year">
					<span>
						<IconButton
							onClick={() => handleStartChange(startYear + 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			<Box sx={{ width: 40 }} />

			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Tooltip title="Previous year">
					<span>
						<IconButton
							onClick={() => handleEndChange(endYear - 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronLeftIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Box sx={{ width: 100 }}>
					<TextField
						label="End"
						value={String(endYear)}
						size="small"
						fullWidth
						slotProps={{
							input: { readOnly: true },
							inputLabel: { shrink: true },
							htmlInput: { style: { textAlign: "center", fontWeight: 600 } },
						}}
					/>
				</Box>
				<Tooltip title="Next year">
					<span>
						<IconButton
							onClick={() => handleEndChange(endYear + 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			<Box sx={{ width: 40 }} />

			<Box sx={{ width: 140 }}>
				<TextField
					select
					label="Mode"
					value={mode}
					onChange={(e) => onModeChange(e.target.value as ForecastMode)}
					size="small"
					fullWidth
					disabled={isLoading}
					slotProps={{ inputLabel: { shrink: true } }}
				>
					<MenuItem value="Actual">Actual</MenuItem>
					<MenuItem value="Forecast">Forecast</MenuItem>
					<MenuItem value="Mixed">Mixed</MenuItem>
				</TextField>
			</Box>
		</Box>
	);
};
